/**
 * **牌背纹理（按尺寸 memoize）。**
 *
 * 牌背没有 `CardDef`，进不了 `CardFaceCache`；但屏幕上的牌背（抽牌堆、旁观扇、
 * 翻牌背面……）都用同一张画法。这里把它统一烘成一张 `pad = 0` 的纹理，
 * 谁要牌背都从这里拿 —— 于是牌背也只有**一份实现**。
 */
import { Rectangle, Texture, type Renderer } from 'pixi.js';
import { backFace, paintClippedFace } from './art';
import type { FaceSize } from './metrics';

const cache = new WeakMap<Renderer, Map<string, Texture>>();

/** 拿到这个渲染器上、这个尺寸的牌背纹理（归缓存所有，别 destroy） */
export function backFaceTexture(renderer: Renderer, size: FaceSize): Texture {
  let map = cache.get(renderer);
  if (map === undefined) {
    map = new Map();
    cache.set(renderer, map);
  }
  const key = size.width + 'x' + size.height;
  const hit = map.get(key);
  if (hit !== undefined) return hit;
  const node = paintClippedFace(backFace(), size);
  // ⚠️ `renderer.generateTexture` 会把逻辑尺寸**截断成整数**（Pixi 源码里的
  //    `region.width = ... | 0`）：68.57 宽的牌会烘出 68 宽的纹理，于是
  //    `CardView` 拿到"比牌还小"的纹理，报"尺寸传反了"。给它一个向上取整的
  //    显式 frame，纹理就永远 ≥ 逻辑尺寸（多出来的那点透明边交给 `clip` 裁掉）。
  const texture = renderer.generateTexture({
    target: node,
    frame: new Rectangle(0, 0, Math.ceil(size.width), Math.ceil(size.height)),
  });
  node.destroy();
  map.set(key, texture);
  return texture;
}
